import React, {useEffect, useState} from "react";
import classNames from "classnames";
import {FlipCardIcon} from "../../assets/svgComponents/flipCardIcon";
import {RoundTripFlightMatch} from "../../models/RoundTripFlightMatch";
import CategoryTagList from "../list/categoryTagList";
import {useTranslation} from "../../i18n/LanguageProvider";
import BookmarkFlightButton from "../shortlist/BookmarkFlightButton";

interface Props {
    roundTripFlightMatch: RoundTripFlightMatch;
    imageUrl: string;
}

const RoundTripCard: React.FC<Props> = ({roundTripFlightMatch, imageUrl}) => {
    const [isFlipped, setIsFlipped] = useState(false);
    const {locale, t} = useTranslation();

    const {round_trip_flight: flight, categories} = roundTripFlightMatch;
    const {
        outbound_flight: outbound,
        inbound_flight: inbound,
        booking_link,
        total_price,
    } = flight;
    const outboundLegs = flight.outbound_flights?.length ? flight.outbound_flights : [outbound];
    const inboundLegs = flight.inbound_flights?.length ? flight.inbound_flights : inbound ? [inbound] : [];
    const hasConnection = outboundLegs.length > 1 || inboundLegs.length > 1;

    // Hot Picks use only the prepared card images, never Unsplash or list crops.
    const fallbackImage = "/images/destinations/card/default.png";
    const [loadedImage, setLoadedImage] = useState<string>();
    const imageSrc = loadedImage === imageUrl ? loadedImage : fallbackImage;

    useEffect(() => {
        let active = true;
        setLoadedImage(undefined);
        if (!imageUrl) return;
        const img = new Image();
        img.onload = () => { if (active) setLoadedImage(imageUrl); };
        img.onerror = () => { if (active) setLoadedImage(undefined); };
        img.src = imageUrl;
        return () => { active = false; };
    }, [imageUrl]);

    const formatDate = (date: string | Date): string => {
        const d = new Date(date);
        return d.toLocaleDateString(locale, {
            day: "2-digit",
            month: "2-digit",
        });
    };


    return (
        <div className="relative w-48">
            <div className="absolute -right-3 -top-3 z-10"><BookmarkFlightButton flight={flight}/></div>
            <div className="h-64">
            {/* Hover wrapper: holds perspective + scale so the flipper remains stable */}
            <div
                className="perspective w-full h-full transition-transform duration-200 hover:scale-[1.03] will-change-transform">
                {/* Flipper: only handles the 3D flip */}
                <div
                    className={classNames(
                        "relative w-full h-full preserve-3d transition-transform duration-700",
                        {"rotate-y-180": isFlipped}
                    )}
                >
                    {/* Front Side */}
                    <div
                        className="absolute w-full h-full rounded-3xl shadow-card ring-2 ring-white bg-cover bg-center text-white flex flex-col justify-between py-8 px-7 backface-hidden"
                        style={{backgroundImage: `url(${imageSrc})`}}
                    >
                        <div className="flex justify-between items-start">
                            <button
                                onClick={() => setIsFlipped(true)}
                                className="bg-white/90 backdrop-blur-md p-2.5 rounded-xl text-gray-800 hover:scale-110 transition"
                            >
                                <FlipCardIcon/>
                            </button>

                            <div className="relative group/price">
                                <div
                                    className="radial-card-bg backdrop-blur-md text-white text-lg font-bold px-3.5 py-1.5 rounded-xl">
                                    {total_price.toFixed(0)},–
                                </div>
                                <span
                                    className="absolute z-10 left-1/2 -translate-x-1/2 -top-20 px-3 py-2 rounded-lg bg-black text-white text-xs font-medium opacity-0 group-hover/price:opacity-100 transition-all duration-200 pointer-events-none text-center whitespace-nowrap">
                                    {t("flight.outbound")}: {outbound.price.toFixed(0)}
                                    {outbound.currency === "EUR" ? "€" : ` ${outbound.currency}`}
                                    <br/>
                                    {inbound ? (
                                        <>
                                            {t("flight.inbound")}: {inbound.price.toFixed(0)}
                                            {inbound.currency === "EUR" ? "€" : ` ${inbound.currency}`}
                                        </>
                                    ) : (
                                        <>{t("flight.oneWay")}</>
                                    )}
                                </span>
                            </div>
                        </div>

                        <div className="flex flex-col gap-4">
                            <div className="text-start">
                                {hasConnection && (
                                    <div className="mb-1 w-fit rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-bold text-[#2D2D2D]">
                                        {t("connection.oneStop")} · {t("connection.selfTransfer")}
                                    </div>
                                )}
                                <h2 className="text-xl text-white font-bold">
                                    {flight.destination_city}
                                </h2>
                                <p className="text-sm font-bold text-white/80">
                                    {inbound
                                        ? `${formatDate(outbound.departure_date)} – ${formatDate(inbound.departure_date)}`
                                        : formatDate(outbound.departure_date)}
                                </p>
                            </div>

                            <div className="flex justify-center w-full">
                                <a
                                    href={booking_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-white text-xs text-[#2D2D2D] font-bold px-6 py-2.5 rounded-full w-full text-center hover:bg-gray-100 transition hover:scale-105"
                                >
                                    {t("flight.book")}
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Back Side */}
                    <div
                        className="absolute w-full h-full rounded-3xl shadow-card ring-2 ring-white bg-white text-gray-800 flex flex-col justify-between py-8 px-5 rotate-y-180 backface-hidden">
                        <div className="flex justify-start items-center gap-4">
                            <button
                                onClick={() => setIsFlipped(false)}
                                className="bg-gray-200 p-2.5 rounded-xl h-fit hover:scale-110 transition"
                            >
                                <FlipCardIcon/>
                            </button>
                            <h2 className="font-bold text-primary text-lg">{flight.destination_city}</h2>
                        </div>

                        <div className="flex flex-col flex-1 mt-7">
                            {hasConnection && (
                                <div className="mb-3 rounded-xl bg-[#F4F2FF] px-3 py-2 text-[10px] font-bold text-accent">
                                    {[outboundLegs, inboundLegs]
                                        .filter((legs) => legs.length)
                                        .map((legs) => legs.map((leg) => `${leg.departure_iata} → ${leg.destination_iata}`).join(" · "))
                                        .join(" / ")}
                                </div>
                            )}
                            <CategoryTagList categories={categories.slice(0, 4)}/>
                        </div>

                        <div className="flex justify-center w-full">
                            <a
                                href={booking_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-accent text-xs text-white font-bold px-6 py-2.5 rounded-full w-full text-center hover:bg-violet-800 transition hover:scale-105"
                            >
                                {t("flight.book")}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
            </div>
        </div>
    );
};

export default RoundTripCard;
